/**
 * Shared <AgentBuilder scope> — Argus Architect canvas.
 *
 * Phase 3 of PR-A AgentBuilder shared-component refactor (2026-05-11).
 * ONE shared component drives both the tenant builder (`/agents/builder`)
 * and the sysadmin builder (`/sysadmin/catalog/agents/builder`) via a
 * scope-discriminated adapter. The route files in `routes/agents/builder.tsx`
 * and `routes/sysadmin/catalog.agents.builder.tsx` collapse to ~30 LOC
 * wrappers that pass `scope`, `preselectModel`, and `agentId` here.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  SCOPE DEVIATION TABLE — every place the two surfaces differ.
 *  See AgentBuilder.types.ts for the canonical version of this table.
 * ───────────────────────────────────────────────────────────────────────────
 *
 *  BUCKET A — Hook swaps (catalog data sources)
 *    tenant.tools          = useWorkspaceTools()      (compose key: t.name)
 *    sysadmin.tools        = useSysadminTools()       (compose key: t.slug)
 *    tenant.skills         = useStudioSkills()        (.items, id as key)
 *    sysadmin.skills       = useCatalogSkills()       (.pages.flatMap, slug as key)
 *    tenant.models         = useTenantArgusModels()   (brandIds; server resolves)
 *    sysadmin.models       = useArgusModels()         (UUIDs; client resolves)
 *    tenant.save           = useCreateAgent()         (create-only, no edit mode)
 *    sysadmin.save         = useCreateCatalogAgent()  + useUpdateCatalogAgent()
 *                                                       + useCatalogAgent()
 *
 *  BUCKET B — Affordances
 *    sysadmin only         : AgentLifecycleActionBar (when editing),
 *                            slug field in identity section,
 *                            "Editing system agent" subtitle,
 *                            edit-mode loading spinner,
 *                            edit-mode seeding useEffect,
 *                            Functions section ENABLED (vs preview banner),
 *                            Workflows section ENABLED (vs preview banner),
 *                            10 sections (vs 8)
 *    tenant only           : ?model= query param preselects brand,
 *                            back → /agents/new (vs /sysadmin/catalog/agents),
 *                            CatalogReferencePicker for tools + models,
 *                            Functions/Workflows pickers DISABLED with banner
 *
 *  BUCKET C — Save payload (12 field differences) — covered by
 *    AgentBuilder.adapters.test.ts and the serializeTenantSavePayload() +
 *    serializeSysadminSavePayload() pure functions in AgentBuilder.adapters.tsx.
 *
 *  BUCKET D — applyMutation switch (16 cases; only 2 deviate)
 *    set_identity   sysadmin auto-derives slug when blank
 *                                              → adapter.transformSetIdentity
 *    set_model      sysadmin resolves brand → UUID via applySetModelMutation
 *                                              → adapter.resolveSetModel
 *    The other 14 cases are byte-identical between scopes.
 *
 * ───────────────────────────────────────────────────────────────────────────
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft, Send, Save, Bot, Sparkles,
  Wrench, BookOpen, Shield, Zap, MessageSquare,
  ChevronDown, Search, Check, Circle, AlertCircle,
  Clock, Eye, Code, Settings2, Settings, Code2,
  Workflow as WorkflowIcon, Loader2, Info, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { ArgusEye } from '@/components/icons';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { IconPicker, AgentIcon } from '@/components/shared/icon-picker';
import { AgentLifecycleActionBar } from '@/components/sysadmin/agent-lifecycle-action-bar';
import {
  type AgentCategory,
  type AgentAutonomy,
  type AgentTrigger,
  type AgentVisibility,
  type AgentModelTask,
  type GuardrailProfile,
  AGENT_CATEGORY_LABELS,
} from '@/lib/mock-agents-data';
import {
  POLICY_LABELS,
  POLICY_COLORS,
} from '@/lib/mock-studio-data';
import { CatalogReferencePicker } from '@/components/ui/catalog-reference-picker';
import {
  useTenantArgusModels,
  type TenantBrand,
} from '@/lib/use-tenant-argus-models';
import { useHelperChat, type PendingMutation } from '@/lib/use-studio-helper';
import { AcceptRejectCard } from '@/components/canvas/accept-reject-card';
import type { CatalogAgentDetail } from '@/lib/use-sysadmin-catalog-agents';
import { PromptApplyDialog } from '@/components/studio/prompts/prompt-apply-dialog';

import {
  type BuilderConfig,
  type BuilderMessage,
  type BuilderPhase,
  type BuilderScope,
  type NormalizedLibraryRef,
  type NormalizedTool,
  INITIAL_CONFIG,
} from './AgentBuilder.types';
import { encodeLibraryRef, getAdapter, seedConfigFromExisting } from './AgentBuilder.adapters';
import { slugify } from './AgentBuilder.helpers';

// ─── Section Status icons / colors ──────────────────────────────────────────

const STATUS_ICON = { empty: Circle, partial: AlertCircle, complete: Check };
const STATUS_COLOR = {
  empty: 'text-muted-foreground/40',
  partial: 'text-amber-500 dark:text-amber-400',
  complete: 'text-emerald-500 dark:text-emerald-400',
};

// ─── Component Props ────────────────────────────────────────────────────────

export interface AgentBuilderProps {
  scope: BuilderScope;
  /** `?model=<brand|uuid>` query-param preselect. Tenant uses brand string;
   *  sysadmin uses argus_models.id UUID. */
  preselectModel?: string;
  /** Sysadmin only — `?id=<agentId>` puts the builder in EDIT mode (PATCH
   *  instead of POST). Ignored on tenant scope. */
  agentId?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function AgentBuilder({ scope, preselectModel, agentId }: AgentBuilderProps) {
  const navigate = useNavigate();
  const adapter = getAdapter(scope);
  const isEditing = scope === 'sysadmin' && !!agentId;

  // ── Catalog data (adapter-normalized) ────────────────────────────────────
  const catalog = adapter.useCatalog();
  const {
    tools,
    skills,
    functions,
    workflows,
    libraryRefs,
    libraryRefsLoading,
    libraryRefsError,
    models,
    modelsLoading,
  } = catalog;

  // ── Existing agent (sysadmin edit mode) ─────────────────────────────────
  const { existing, loading: loadingExisting } = adapter.useExistingAgent(agentId);

  // ── Save mutation ────────────────────────────────────────────────────────
  const { saveInFlight, save } = adapter.useSaveAgent();

  // ── Local UI state ───────────────────────────────────────────────────────
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');
  const [phase, setPhase] = useState<BuilderPhase>('discover');
  const [config, setConfig] = useState<BuilderConfig>(() => ({
    ...INITIAL_CONFIG,
    modelId: preselectModel ?? INITIAL_CONFIG.modelId,
    modelIds: preselectModel ? [preselectModel] : INITIAL_CONFIG.modelIds,
  }));
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([1]));
  const [promptPreview, setPromptPreview] = useState(false);
  // #151 — open state for the Apply prompt template dialog. Clicking
  // "Apply template" in the System Prompt section opens the dialog;
  // the dialog's onApply callback stuffs the substituted body into
  // config.systemPrompt (overwriting whatever was there).
  const [promptApplyOpen, setPromptApplyOpen] = useState(false);
  const [toolSearch, setToolSearch] = useState('');
  const [functionSearch, setFunctionSearch] = useState('');
  const [workflowSearch, setWorkflowSearch] = useState('');

  // ── Live AI chat — Day 3-4 §6.4 ──────────────────────────────────────────
  const helper = useHelperChat({
    kind: 'architect',
    scope: adapter.helperScope,
    canvasState: config as unknown as Record<string, unknown>,
  });

  // Static welcome turn. Both scopes use the same intro.
  const introMessage: BuilderMessage = {
    id: 'm-intro',
    role: 'argus',
    content: "Hi! I'm the **Argus Architect** — I'll help you design and build a custom agent.\n\nTell me about the problem you want this agent to solve. I'll ask a few clarifying questions, then draft a complete agent (identity, prompt, tools, skills, guardrails, triggers) — you'll see each suggestion as an Accept/Reject card you can apply selectively.\n\n**What should this agent do?**",
    chips: [
      { id: 'c1', label: 'Monitor equipment', emoji: '🔧', action: 'I need an agent that monitors equipment health and alerts when something looks wrong' },
      { id: 'c2', label: 'Generate reports', emoji: '📊', action: 'I need an agent that generates shift handover reports automatically' },
      { id: 'c3', label: 'Answer questions', emoji: '💬', action: 'I need an agent that answers questions about our SOPs and procedures' },
      { id: 'c4', label: 'Track compliance', emoji: '🛡️', action: 'I need an agent that monitors regulatory compliance and tracks certifications' },
    ],
  };

  const messages: BuilderMessage[] = [
    introMessage,
    ...helper.messages.map((m, idx) => ({
      id: `m-live-${idx}`,
      role: (m.role === 'user' ? 'user' : 'argus') as BuilderMessage['role'],
      content: m.content,
    })),
  ];

  // ── Edit-mode seeding (sysadmin only) ────────────────────────────────────
  useEffect(() => {
    if (!existing) return;
    setConfig((prev) => seedConfigFromExisting(existing, prev));
    setOpenSections(new Set([1, 2, 4, 5, 6]));
    setPhase('design');
  }, [existing]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [helper.messages, helper.isStreaming]);

  // ── Config updates ───────────────────────────────────────────────────────
  const updateConfig = (partial: Partial<BuilderConfig>) =>
    setConfig((prev) => ({ ...prev, ...partial }));

  const toggleSection = (n: number) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });

  // ── apply-mutation switch ────────────────────────────────────────────────
  //
  // 14 of 16 cases are identical between scopes. The 2 deviating cases
  // (set_identity slug-derive, set_model brand→UUID) go through adapter
  // seams. See Bucket D in the scope-deviation table above.
  const applyMutation = (m: PendingMutation) => {
    const a = m.args as Record<string, unknown>;
    switch (m.toolName) {
      case 'set_identity':
        updateConfig(adapter.transformSetIdentity(a, config));
        break;
      case 'set_system_prompt':
        updateConfig({ systemPrompt: String(a.prompt ?? '') });
        break;
      case 'set_model': {
        const result = adapter.resolveSetModel(a, models, modelsLoading);
        if (result.loading) {
          toast.info('Models catalog still loading', {
            description: 'Try again in a moment to apply this suggestion.',
          });
          return; // do NOT consume — leave mutation pending for retry
        }
        if (!result.patch) {
          toast.error('Unknown model brand', { description: result.error ?? '' });
          break;
        }
        updateConfig(result.patch);
        break;
      }
      case 'add_tool':
        updateConfig({
          selectedTools: Array.from(new Set([...config.selectedTools, String(a.slug)])),
        });
        break;
      case 'remove_tool':
        updateConfig({
          selectedTools: config.selectedTools.filter((s) => s !== String(a.slug)),
        });
        break;
      case 'add_skill':
        updateConfig({
          selectedSkills: Array.from(new Set([...config.selectedSkills, String(a.slug)])),
        });
        break;
      case 'remove_skill':
        updateConfig({
          selectedSkills: config.selectedSkills.filter((s) => s !== String(a.slug)),
        });
        break;
      case 'add_function':
        updateConfig({
          selectedFunctions: Array.from(new Set([...config.selectedFunctions, String(a.slug)])),
        });
        break;
      case 'remove_function':
        updateConfig({
          selectedFunctions: config.selectedFunctions.filter((s) => s !== String(a.slug)),
        });
        break;
      case 'add_knowledge':
        updateConfig({
          knowledgeSources: Array.from(new Set([
            ...config.knowledgeSources,
            typeof a.kind === 'string' && typeof a.id === 'string'
              ? encodeLibraryRef({
                  kind: a.kind as NormalizedLibraryRef['kind'],
                  id: a.id,
                })
              : String(a.collection),
          ])),
        });
        break;
      case 'remove_knowledge':
        updateConfig({
          knowledgeSources: config.knowledgeSources.filter((s) => {
            const key = typeof a.kind === 'string' && typeof a.id === 'string'
              ? encodeLibraryRef({
                  kind: a.kind as NormalizedLibraryRef['kind'],
                  id: a.id,
                })
              : String(a.collection);
            return s !== key;
          }),
        });
        break;
      case 'set_guardrail_profile':
        updateConfig({ guardrailProfile: a.profile as GuardrailProfile });
        break;
      case 'set_forbidden_topics':
        updateConfig({
          forbiddenTopics: Array.isArray(a.topics) ? (a.topics as unknown[]).map(String) : [],
        });
        break;
      case 'set_autonomy':
        updateConfig({ autonomyLevel: a.level as AgentAutonomy });
        break;
      case 'set_triggers':
        updateConfig({
          triggerModes: Array.isArray(a.modes)
            ? (a.modes as unknown[]).map((x) => x as AgentTrigger)
            : ['conversational'],
        });
        break;
      case 'set_schedule':
        updateConfig({ cronExpression: String(a.cron ?? '') });
        break;
    }
    helper.consumeMutation(m.callId);
  };

  const applyAll = () => {
    for (const m of helper.pendingMutations) {
      if (m.confirmed && m.turnComplete && !m.invalidReason) applyMutation(m);
    }
  };

  // ── Hallucinated-slug guard ──────────────────────────────────────────────
  //
  // fix/argus-live-publish-cluster: depend on the STABLE primitives only.
  // `helper` is a fresh object literal on every render (the hook returns
  // a new `{ messages, send, ... }` shape each call), so listing it as a
  // dep would re-fire this effect on every render — wasted work plus a
  // latent loop risk if any branch in the body ever stops being purely
  // additive. `helper.markMutationInvalid` is `useCallback`'d inside
  // `useHelperChat` so its reference IS stable; we keep it out of the
  // dep array since it never changes. Same reasoning for `adapter`,
  // which is the return value of `getAdapter(scope)` — `scope` is the
  // real reactive dep but it's a const string for the lifetime of this
  // component instance.
  useEffect(() => {
    for (const m of helper.pendingMutations) {
      if (m.invalidReason) continue;
      const a = m.args as Record<string, unknown>;
      if (m.toolName === 'add_tool' && a.slug) {
        const slug = String(a.slug);
        if (!adapter.validateAddTool(slug, tools)) {
          helper.markMutationInvalid(m.callId, 'Tool not found in catalog');
        }
      } else if (m.toolName === 'add_skill' && a.slug) {
        const slug = String(a.slug);
        if (!adapter.validateAddSkill(slug, skills)) {
          helper.markMutationInvalid(m.callId, 'Skill not found in catalog');
        }
      } else if (m.toolName === 'add_function' && a.slug && adapter.validateAddFunction) {
        const slug = String(a.slug);
        if (!adapter.validateAddFunction(slug, functions)) {
          helper.markMutationInvalid(m.callId, 'Function not found in catalog');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helper.pendingMutations, tools, skills, functions]);

  // ── Toggle helpers (used by inline checkbox grids) ──────────────────────
  const toggleTool = (slug: string) => {
    setConfig((prev) => ({
      ...prev,
      selectedTools: prev.selectedTools.includes(slug)
        ? prev.selectedTools.filter((t) => t !== slug)
        : [...prev.selectedTools, slug],
    }));
  };
  const toggleFunction = (key: string) => {
    setConfig((prev) => ({
      ...prev,
      selectedFunctions: prev.selectedFunctions.includes(key)
        ? prev.selectedFunctions.filter((f) => f !== key)
        : [...prev.selectedFunctions, key],
    }));
  };
  const toggleSkill = (slug: string) => {
    setConfig((prev) => ({
      ...prev,
      selectedSkills: prev.selectedSkills.includes(slug)
        ? prev.selectedSkills.filter((s) => s !== slug)
        : [...prev.selectedSkills, slug],
    }));
  };
  const toggleWorkflow = (slug: string) => {
    setConfig((prev) => ({
      ...prev,
      selectedWorkflows: prev.selectedWorkflows.includes(slug)
        ? prev.selectedWorkflows.filter((w) => w !== slug)
        : [...prev.selectedWorkflows, slug],
    }));
  };
  const toggleTrigger = (trigger: AgentTrigger) => {
    setConfig((prev) => ({
      ...prev,
      triggerModes: prev.triggerModes.includes(trigger)
        ? prev.triggerModes.filter((t) => t !== trigger)
        : [...prev.triggerModes, trigger],
    }));
  };

  // ── Completion stats ─────────────────────────────────────────────────────
  const completedSections = adapter.sectionIndices.filter(
    (n) => adapter.getSectionStatus(config, n) === 'complete',
  ).length;
  const progressPercent = Math.round((completedSections / adapter.totalSections) * 100);
  const canSave = adapter.canSave(config, isEditing);

  // ── Conversation handler ─────────────────────────────────────────────────
  const handleSend = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || helper.isStreaming) return;
    setInput('');
    if (phase === 'discover' && helper.pendingMutations.length > 0) setPhase('design');
    if (phase === 'design' && /\b(save|ready|good|looks great|done)\b/i.test(msg)) {
      setPhase('ready');
    } else if (phase === 'design') {
      setPhase('refine');
    }
    await helper.send(msg);
  };

  const lastChipsMsg = [...messages].reverse().find((m) => m.chips);
  const chipsUsed = lastChipsMsg
    ? messages.some(
        (m) => m.role === 'user' && messages.indexOf(m) > messages.indexOf(lastChipsMsg),
      )
    : true;

  // ── Filtered pickers (used by sysadmin checkbox grids) ──────────────────
  const filteredTools = tools.filter((t) => {
    if (!toolSearch) return true;
    const q = toolSearch.toLowerCase();
    return t.slug.toLowerCase().includes(q) || t.displayName.toLowerCase().includes(q);
  }).slice(0, 30);

  const filteredFunctions = (functions ?? []).filter((f) => {
    if (!functionSearch) return true;
    const q = functionSearch.toLowerCase();
    return f.slug.toLowerCase().includes(q) || f.displayName.toLowerCase().includes(q);
  }).slice(0, 30);

  const filteredWorkflows = (workflows ?? []).filter((w) => {
    if (!workflowSearch) return true;
    const q = workflowSearch.toLowerCase();
    return w.slug.toLowerCase().includes(q) || w.displayName.toLowerCase().includes(q);
  }).slice(0, 30);

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSave = () => {
    if (!canSave) return;
    save({
      config,
      isEditing,
      editId: agentId,
      onSuccess: (createdId) => {
        toast.success(`Agent "${config.name}" ${isEditing ? 'saved' : 'created'}`);
        if (scope === 'tenant') {
          navigate({ to: '/agents' });
        } else if (createdId) {
          // Sysadmin create: move into edit mode so lifecycle bar appears.
          navigate({
            to: '/sysadmin/catalog/agents/builder',
            search: { id: createdId, model: undefined },
          });
        }
      },
      onError: (e) => toast.error(`${isEditing ? 'Save' : 'Create'} failed`, { description: e.message }),
    });
  };

  // ── Back handler ─────────────────────────────────────────────────────────
  const handleBack = () => {
    if (!isEditing && (config.name || config.systemPrompt) && !confirm('You have unsaved changes. Discard?')) {
      return;
    }
    navigate({ to: adapter.backRoute });
  };

  // ── Edit-mode loading state (sysadmin only) ──────────────────────────────
  if (isEditing && loadingExisting) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2 inline" /> Loading agent…
      </div>
    );
  }

  // ── Section numbering ────────────────────────────────────────────────────
  //
  // Sysadmin has 10 sections; tenant has 8. Use stable per-scope numbering
  // (so the SectionCard `number` prop matches getSectionStatus()'s switch).
  // Tenant maps:   Identity=1, Prompt=2, Model=3, Tools=4, Skills=5,
  //                Guardrails=6, Triggers=7, Advanced=8
  // Sysadmin maps: Identity=1, Prompt=2, Model=3, Tools=4, Functions=5,
  //                Skills=6, Guardrails=7, Workflows=8, Triggers=9, Advanced=10
  const sectionMap = scope === 'sysadmin'
    ? { identity: 1, prompt: 2, model: 3, tools: 4, functions: 5, skills: 6, guardrails: 7, workflows: 8, triggers: 9, advanced: 10 }
    : { identity: 1, prompt: 2, model: 3, tools: 4, skills: 5, guardrails: 6, triggers: 7, advanced: 8 };

  // Stub indices for sections that don't exist on tenant scope — these
  // section cards aren't rendered at all on tenant, but the picker UI
  // for Functions/Workflows still appears as a "preview" gated picker.
  const tenantFunctionsSection = 4; // shares open-state with Tools section
  const tenantWorkflowsSection = 6; // shares open-state with Guardrails section

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-4 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={scope === 'sysadmin' ? 'Back to agent catalog' : 'Back'}
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <ArgusEye state="idle" className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-sm font-semibold">Argus Architect</h1>
            <p className="text-[10px] text-muted-foreground">{adapter.subtitle(isEditing)}</p>
          </div>
          <Badge variant="outline" className={cn('text-[9px]',
            phase === 'discover' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
            phase === 'design' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' :
            phase === 'refine' ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400' :
            'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
          )}>
            {phase === 'discover' ? 'Discovering' : phase === 'design' ? 'Designing' : phase === 'refine' ? 'Refining' : 'Ready'}
          </Badge>
          {config.name && (
            <div className="flex items-center gap-1.5">
              <AgentIcon iconName={config.icon} colorName={config.iconColor} size="sm" />
              <Badge variant="secondary" className="text-[9px]">{config.name}</Badge>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">{completedSections}/{adapter.totalSections} sections</span>
          <Progress value={progressPercent} className="w-20 h-1.5" />
          <Button
            size="sm"
            className="gap-1.5"
            disabled={!canSave || saveInFlight}
            onClick={handleSave}
          >
            <Save className="h-3.5 w-3.5" />
            {saveInFlight ? 'Saving…' : isEditing ? 'Save Changes' : 'Save Agent'}
          </Button>
        </div>
      </div>

      {/* Lifecycle action bar — sysadmin-only. Bucket B affordance. */}
      {adapter.showLifecycleBar && isEditing && existing && (
        <AgentLifecycleActionBar agent={existing.raw as CatalogAgentDetail} />
      )}

      {/* Split layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Conversation */}
        <div className="w-[440px] shrink-0 flex flex-col border-r">
          <div className="px-3 py-2 border-b bg-emerald-500/5 text-[10px] text-emerald-700 dark:text-emerald-400 leading-snug shrink-0 flex items-center gap-2">
            <Sparkles className="h-3 w-3" />
            <span>
              <strong>Live AI</strong> · Argus Architect drafts suggestions you Accept or Reject below.
            </span>
          </div>
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {messages.map((msg) => {
              if (msg.role === 'argus') return (
                <div key={msg.id}>
                  <div className="flex gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <ArgusEye state="idle" className="h-5 w-5 text-primary" />
                    </div>
                    <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[88%]">
                      <MarkdownRenderer content={msg.content} />
                    </div>
                  </div>
                  {msg.chips && msg === lastChipsMsg && !chipsUsed && (
                    <div className="flex flex-wrap gap-1.5 ml-9 mt-1.5">
                      {msg.chips.map((chip) => (
                        <button
                          key={chip.id}
                          onClick={() => handleSend(chip.action)}
                          disabled={helper.isStreaming}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/40 active:scale-95 transition-all disabled:opacity-50"
                        >
                          {chip.emoji && <span className="text-sm">{chip.emoji}</span>}
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
              if (msg.role === 'system') return (
                <div key={msg.id} className="text-center">
                  <span className="text-[10px] text-muted-foreground/60 italic">{msg.content}</span>
                </div>
              );
              return (
                <div key={msg.id} className="flex justify-end">
                  <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%] text-sm">{msg.content}</div>
                </div>
              );
            })}
            {helper.isStreaming && (
              <div className="flex gap-2.5">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <ArgusEye state="thinking" blink="fast" className="h-5 w-5 text-primary" />
                </div>
                <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {helper.pendingMutations.length > 0 && (
            <div className="px-3 pt-2 shrink-0">
              <AcceptRejectCard
                mutations={helper.pendingMutations}
                helperLabel="Argus Architect"
                applyMutation={applyMutation}
                rejectMutation={(m) => helper.consumeMutation(m.callId)}
                applyAll={applyAll}
                rejectAll={() => helper.clearMutations()}
              />
            </div>
          )}
          <div className="p-3 border-t flex gap-2 shrink-0">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Describe your agent or ask for changes..."
              className="min-h-[40px] max-h-[100px] resize-none text-sm"
              rows={1}
              disabled={helper.isStreaming || helper.pendingMutations.length > 0}
            />
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || helper.isStreaming || helper.pendingMutations.length > 0}
              size="icon"
              className="h-10 w-10 shrink-0 rounded-xl"
              aria-label="Send message"
              title={helper.pendingMutations.length > 0 ? 'Resolve pending suggestions first' : 'Send'}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Right: Interactive Canvas */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {/* Section 1: Identity */}
          <SectionCard
            title="Identity"
            icon={Bot}
            number={sectionMap.identity}
            status={adapter.getSectionStatus(config, sectionMap.identity)}
            open={openSections.has(sectionMap.identity)}
            onToggle={() => toggleSection(sectionMap.identity)}
          >
            <div className="space-y-3">
              <div className="grid grid-cols-[1fr_240px] gap-4">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      Name<span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    <Input
                      value={config.name}
                      onChange={(e) => {
                        // Sysadmin auto-derives slug when blank; tenant scope
                        // ignores slug entirely.
                        if (adapter.showSlugField) {
                          updateConfig({
                            name: e.target.value,
                            slug: config.slug || (e.target.value ? slugify(e.target.value) : ''),
                          });
                        } else {
                          updateConfig({ name: e.target.value });
                        }
                      }}
                      className="h-8 text-xs"
                      placeholder="Agent name"
                    />
                  </div>
                  {adapter.showSlugField && (
                    <div className="space-y-1">
                      <Label className="text-xs">
                        Slug<span className="text-red-500 ml-0.5">*</span>
                      </Label>
                      <Input
                        value={config.slug}
                        onChange={(e) => updateConfig({ slug: e.target.value })}
                        className="h-8 text-xs font-mono"
                        placeholder="agent-slug"
                        disabled={isEditing}
                        title={isEditing ? 'Slug is immutable after creation' : 'Catalog identity key — kebab-case'}
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Textarea
                      value={config.description}
                      onChange={(e) => updateConfig({ description: e.target.value })}
                      rows={2}
                      className="text-xs"
                      placeholder="What does this agent do?"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Category</Label>
                      <Select
                        value={config.category}
                        onValueChange={(v) => updateConfig({ category: v as AgentCategory })}
                      >
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(AGENT_CATEGORY_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Visibility</Label>
                      <Select
                        value={config.visibility}
                        onValueChange={(v) => updateConfig({ visibility: v as AgentVisibility })}
                      >
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="private">Private</SelectItem>
                          <SelectItem value="team">Team</SelectItem>
                          <SelectItem value="org_wide">Organization</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div>
                  <IconPicker
                    value={{ icon: config.icon, color: config.iconColor }}
                    onChange={(v) => updateConfig({ icon: v.icon, iconColor: v.color })}
                  />
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Section 2: System Prompt */}
          <SectionCard
            title="System Prompt"
            icon={Code}
            number={sectionMap.prompt}
            status={adapter.getSectionStatus(config, sectionMap.prompt)}
            open={openSections.has(sectionMap.prompt)}
            onToggle={() => toggleSection(sectionMap.prompt)}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant={promptPreview ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-6 text-[10px] gap-1"
                    onClick={() => setPromptPreview(!promptPreview)}
                  >
                    <Eye className="h-3 w-3" /> {promptPreview ? 'Edit' : 'Preview'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1"
                    onClick={() => handleSend('Generate a detailed system prompt for this agent')}
                  >
                    <Sparkles className="h-3 w-3" /> Generate with AI
                  </Button>
                  {/*
                    #151 — "Apply template" button. Opens the
                    PromptApplyDialog which lets the user pick a prompt
                    template (tenant-owned, system, or cloned), fill in
                    its typed variables, preview the substituted body,
                    and write it into the agent's systemPrompt field.
                    Uses the same usePromptTemplates() source the Studio
                    Prompts catalog renders from — anything the user
                    can see there, they can apply here.
                  */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1"
                    onClick={() => setPromptApplyOpen(true)}
                    title="Apply a saved prompt template"
                  >
                    <FileText className="h-3 w-3" /> Apply template
                  </Button>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {config.systemPrompt.length} chars · {config.systemPrompt.split(/\s+/).filter(Boolean).length} words
                </span>
              </div>
              {promptPreview ? (
                <div className="bg-muted/30 rounded-lg p-4 min-h-[200px] prose prose-sm dark:prose-invert max-w-none">
                  {config.systemPrompt ? <MarkdownRenderer content={config.systemPrompt} /> : <p className="text-muted-foreground italic">No system prompt yet</p>}
                </div>
              ) : (
                <Textarea
                  value={config.systemPrompt}
                  onChange={(e) => updateConfig({ systemPrompt: e.target.value })}
                  rows={10}
                  className="font-mono text-xs"
                  placeholder="Define the agent's behavior, rules, and capabilities..."
                />
              )}
              <p className="text-[10px] text-muted-foreground">
                Recommended: 200-1000 words. Use markdown for structure. Available variables: {'{{USER_NAME}}'}, {'{{CURRENT_DATE}}'}, {'{{FACILITY_NAME}}'}
              </p>
            </div>
          </SectionCard>

          {/* Section 3: Model & Behavior */}
          <SectionCard
            title="Model & Behavior"
            icon={Settings2}
            number={sectionMap.model}
            status={adapter.getSectionStatus(config, sectionMap.model)}
            open={openSections.has(sectionMap.model)}
            onToggle={() => toggleSection(sectionMap.model)}
          >
            <div className="space-y-3">
              {adapter.showCatalogPickerForModels ? (
                // Tenant: CatalogReferencePicker over useTenantArgusModels().
                <CatalogReferencePicker
                  leafKind="model"
                  label="Models"
                  description="Pick one or more models this agent can run against. The first is the primary; the runtime routes by task across the full set. Models are assigned to your tenant by an administrator — ask if a model is missing."
                  multiSelect
                  value={config.modelIds}
                  onChange={(next) => updateConfig({
                    modelIds: next,
                    modelId: next[0] ?? config.modelId,
                  })}
                  source={{
                    useItems: () => {
                      const q = useTenantArgusModels();
                      return {
                        data: q.data?.items as TenantBrand[] | undefined,
                        isLoading: q.isLoading,
                        error: q.error ?? undefined,
                      };
                    },
                    getId: (b: TenantBrand) => b.brandId,
                    getLabel: (b: TenantBrand) => b.displayName,
                    getDescription: (b: TenantBrand) =>
                      `${b.modality}${b.contextWindowK ? ` · ${b.contextWindowK}k ctx` : ''}${b.assignedRoleLabel ? ` · ${b.assignedRoleLabel}` : ''}`,
                    getStatus: (b: TenantBrand) => b.runtimeState,
                  }}
                />
              ) : (
                // Sysadmin: native <Select> sourced from useArgusModels().
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Model</Label>
                    <Select
                      value={config.modelId}
                      onValueChange={(v) => updateConfig({ modelId: v, modelIds: v ? [v] : [] })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select model…" /></SelectTrigger>
                      <SelectContent>
                        {models.length === 0 ? (
                          <SelectItem value="__none__" disabled>No models — visit Sysadmin → Models</SelectItem>
                        ) : (
                          models
                            .filter((m) => m.isActive)
                            .map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.displayName} <span className="text-muted-foreground ml-1">({m.brandId})</span>
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Task</Label>
                    <Select
                      value={config.modelTask}
                      onValueChange={(v) => updateConfig({ modelTask: v as AgentModelTask })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chat">Chat</SelectItem>
                        <SelectItem value="classify">Classify</SelectItem>
                        <SelectItem value="code">Code</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Max Steps</Label>
                    <Input
                      type="number"
                      value={config.maxSteps}
                      onChange={(e) => updateConfig({ maxSteps: Number(e.target.value) })}
                      className="h-8 text-xs"
                      min={1}
                      max={50}
                    />
                  </div>
                </div>
              )}
              {/* Tenant scope: Task + Max Steps row sits below the CatalogReferencePicker. */}
              {adapter.showCatalogPickerForModels && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Task</Label>
                    <Select
                      value={config.modelTask}
                      onValueChange={(v) => updateConfig({ modelTask: v as AgentModelTask })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chat">Chat</SelectItem>
                        <SelectItem value="classify">Classify</SelectItem>
                        <SelectItem value="code">Code</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Max Steps</Label>
                    <Input
                      type="number"
                      value={config.maxSteps}
                      onChange={(e) => updateConfig({ maxSteps: Number(e.target.value) })}
                      className="h-8 text-xs"
                      min={1}
                      max={50}
                    />
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Autonomy Level</Label>
                <RadioGroup
                  value={config.autonomyLevel}
                  onValueChange={(v) => updateConfig({ autonomyLevel: v as AgentAutonomy })}
                  className="grid grid-cols-2 gap-2"
                >
                  {([
                    { value: 'read_only', label: 'Read Only', desc: 'Can only read data' },
                    { value: 'assisted', label: 'Assisted', desc: 'Reads freely, writes suggested' },
                    { value: 'approval_required', label: 'Approval Required', desc: 'Writes need human approval' },
                    { value: 'auto_execute', label: 'Auto Execute', desc: 'Fully autonomous' },
                  ] as const).map((opt) => (
                    <label
                      key={opt.value}
                      className={cn(
                        'flex items-start gap-2 p-2 rounded-lg border cursor-pointer text-xs',
                        config.autonomyLevel === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30',
                      )}
                    >
                      <RadioGroupItem value={opt.value} className="mt-0.5" />
                      <div>
                        <p className="font-medium">{opt.label}</p>
                        <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <Separator />
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Temperature</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={config.temperature}
                      onChange={(e) => updateConfig({ temperature: Number(e.target.value) })}
                      className="h-8 text-xs"
                      min={0}
                      max={1}
                      step={0.1}
                    />
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {config.temperature < 0.3 ? 'Precise' : config.temperature > 0.7 ? 'Creative' : 'Balanced'}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Max Output Tokens</Label>
                  <Input
                    type="number"
                    value={config.maxOutputTokens}
                    onChange={(e) => updateConfig({ maxOutputTokens: Number(e.target.value) })}
                    className="h-8 text-xs"
                    min={128}
                    max={16384}
                    step={256}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Response Format</Label>
                  <Select
                    value={config.responseFormat}
                    onValueChange={(v) => updateConfig({ responseFormat: v as 'markdown' | 'json' | 'plain' })}
                  >
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="markdown">Markdown</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="plain">Plain Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Section 4: Tools */}
          <SectionCard
            title={`Tools (${config.selectedTools.length})`}
            icon={Wrench}
            number={sectionMap.tools}
            status={adapter.getSectionStatus(config, sectionMap.tools)}
            open={openSections.has(sectionMap.tools)}
            onToggle={() => toggleSection(sectionMap.tools)}
          >
            <div className="space-y-3">
              {adapter.showCatalogPickerForTools ? (
                <>
                  <CatalogReferencePicker
                    leafKind="tool"
                    label=""
                    description="Browse the workspace tool catalog. Selected tools are wired into the agent's MCP gateway scope; the agent never sees a tool that isn't in this set."
                    multiSelect
                    value={config.selectedTools}
                    onChange={(next) => updateConfig({ selectedTools: next })}
                    source={{
                      useItems: () => ({
                        data: tools,
                        isLoading: false,
                        error: undefined,
                      }),
                      getId: (t: NormalizedTool) => t.slug,
                      getLabel: (t: NormalizedTool) => t.slug,
                      getDescription: (t: NormalizedTool) =>
                        `${POLICY_LABELS[t.policy as keyof typeof POLICY_LABELS] ?? t.policy}`,
                    }}
                  />
                  <details className="text-[10px]">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      Quick-pick (legacy checkbox grid)
                    </summary>
                    <div className="relative mt-2">
                      <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input value={toolSearch} onChange={(e) => setToolSearch(e.target.value)} placeholder="Search tools..." className="h-7 text-xs pl-8" />
                    </div>
                    <div className="max-h-[250px] overflow-auto space-y-1 mt-2">
                      {filteredTools.map((tool) => (
                        <label
                          key={tool.id}
                          className={cn(
                            'flex items-center gap-2 p-1.5 rounded hover:bg-muted/30 cursor-pointer text-xs',
                            config.selectedTools.includes(tool.slug) && 'bg-primary/5',
                          )}
                        >
                          <Checkbox
                            checked={config.selectedTools.includes(tool.slug)}
                            onCheckedChange={() => toggleTool(tool.slug)}
                          />
                          <span className="font-mono text-[11px] flex-1 truncate">{tool.slug}</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-[8px]',
                              POLICY_COLORS[tool.policy as keyof typeof POLICY_COLORS],
                            )}
                          >
                            {POLICY_LABELS[tool.policy as keyof typeof POLICY_LABELS]}
                          </Badge>
                        </label>
                      ))}
                    </div>
                  </details>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1"
                    onClick={() => handleSend('Suggest the best tools for this agent based on its purpose')}
                  >
                    <Sparkles className="h-3 w-3" /> Suggest Tools
                  </Button>
                </>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input value={toolSearch} onChange={(e) => setToolSearch(e.target.value)} placeholder="Search tools..." className="h-7 text-xs pl-8" />
                  </div>
                  <div className="max-h-[250px] overflow-auto space-y-1">
                    {filteredTools.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground italic px-1">
                        No platform tools yet. Author one at <span className="font-medium">Sysadmin → Catalog → Tools</span>.
                      </p>
                    ) : filteredTools.map((tool) => (
                      <label
                        key={tool.id}
                        className={cn(
                          'flex items-center gap-2 p-1.5 rounded hover:bg-muted/30 cursor-pointer text-xs',
                          config.selectedTools.includes(tool.slug) && 'bg-primary/5',
                        )}
                      >
                        <Checkbox
                          checked={config.selectedTools.includes(tool.slug)}
                          onCheckedChange={() => toggleTool(tool.slug)}
                        />
                        <span className="font-mono text-[11px] flex-1 truncate" title={tool.displayName}>{tool.slug}</span>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[8px]',
                            POLICY_COLORS[tool.policy as keyof typeof POLICY_COLORS],
                          )}
                        >
                          {POLICY_LABELS[tool.policy as keyof typeof POLICY_LABELS]}
                        </Badge>
                      </label>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1"
                    onClick={() => handleSend('Suggest the best tools for this agent based on its purpose')}
                  >
                    <Sparkles className="h-3 w-3" /> Suggest Tools
                  </Button>
                </>
              )}
            </div>
          </SectionCard>

          {/* Section 4.5 / 5: Functions — Bucket B affordance.
            * Sysadmin: enabled checkbox grid (sectionMap.functions = 5).
            * Tenant: disabled CatalogReferencePicker with "preview" banner
            *          (shared open-state with Tools section). */}
          {adapter.showFunctionsPicker === 'enabled' && (
            <SectionCard
              title={`Functions (${config.selectedFunctions.length})`}
              icon={Code2}
              number={sectionMap.functions!}
              status={adapter.getSectionStatus(config, sectionMap.functions!)}
              open={openSections.has(sectionMap.functions!)}
              onToggle={() => toggleSection(sectionMap.functions!)}
            >
              <div className="space-y-3">
                <p className="text-[10px] text-muted-foreground">
                  Wrapper functions composed into the agent's chain — inlets run before generation, outlets after. Sysadmin authors these in the function library.
                </p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input value={functionSearch} onChange={(e) => setFunctionSearch(e.target.value)} placeholder="Search functions..." className="h-7 text-xs pl-8" />
                </div>
                <div className="max-h-[250px] overflow-auto space-y-1">
                  {filteredFunctions.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic px-1">
                      No published functions yet. Author + publish at <span className="font-medium">Sysadmin → Catalog → Functions</span>.
                    </p>
                  ) : filteredFunctions.map((fn) => (
                    <label
                      key={fn.id}
                      className={cn(
                        'flex items-center gap-2 p-1.5 rounded hover:bg-muted/30 cursor-pointer text-xs',
                        config.selectedFunctions.includes(fn.slug) && 'bg-primary/5',
                      )}
                    >
                      <Checkbox
                        checked={config.selectedFunctions.includes(fn.slug)}
                        onCheckedChange={() => toggleFunction(fn.slug)}
                      />
                      <span className="font-mono text-[11px] flex-1 truncate" title={fn.displayName}>{fn.slug}</span>
                      <Badge variant="outline" className="text-[8px] capitalize">{fn.appliesTo}</Badge>
                    </label>
                  ))}
                </div>
              </div>
            </SectionCard>
          )}
          {adapter.showFunctionsPicker === 'disabled-preview' && (
            <SectionCard
              title={`Functions (${config.selectedFunctions.length})`}
              icon={Code2}
              number={4.5 as unknown as number}
              status={config.selectedFunctions.length > 0 ? 'complete' : 'empty'}
              open={openSections.has(tenantFunctionsSection)}
              onToggle={() => toggleSection(tenantFunctionsSection)}
            >
              <div className="space-y-2">
                <div
                  className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300 leading-snug"
                  role="status"
                >
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                  <div>
                    <span className="font-medium">Functions catalog (preview).</span>{' '}
                    Tenant-scoped function selection is coming in v1.1. In the meantime, your administrator can grant tools and skills directly through the sysadmin catalog.
                  </div>
                </div>
                <CatalogReferencePicker
                  leafKind="function"
                  label=""
                  description="Wrapper functions hook into the agent's inlet/outlet pipeline (PII redaction, classification, format gates). Published by the platform — read-only for tenants."
                  multiSelect
                  value={config.selectedFunctions}
                  onChange={(next) => updateConfig({ selectedFunctions: next })}
                  disabled
                  source={{
                    useItems: () => ({
                      data: functions ?? [],
                      isLoading: false,
                      error: undefined,
                    }),
                    getId: (f) => f.slug,
                    getLabel: (f) => f.displayName,
                    getDescription: (f) => `${f.slug} · ${f.category} · ${f.runtime}`,
                    getStatus: (f) => f.status,
                  }}
                />
              </div>
            </SectionCard>
          )}

          {/* Section 5/6: Skills */}
          <SectionCard
            title={`Skills (${config.selectedSkills.length})`}
            icon={BookOpen}
            number={sectionMap.skills}
            status={adapter.getSectionStatus(config, sectionMap.skills)}
            open={openSections.has(sectionMap.skills)}
            onToggle={() => toggleSection(sectionMap.skills)}
          >
            <div className="space-y-2">
              {scope === 'tenant' ? (
                catalog.skillsEmpty ? (
                  <p className="text-[11px] text-muted-foreground italic">
                    No skills authored yet. Create one in <span className="font-medium">Studio → Skills</span>.
                  </p>
                ) : (
                  <>
                    <CatalogReferencePicker
                      leafKind="skill"
                      label=""
                      description="Skills are reusable instruction bundles. Selected skills auto-grant their declared tool dependencies."
                      multiSelect
                      value={config.selectedSkills}
                      onChange={(next) => updateConfig({ selectedSkills: next })}
                      source={{
                        useItems: () => ({
                          data: skills,
                          isLoading: false,
                          error: undefined,
                        }),
                        getId: (s) => s.slug,
                        getLabel: (s) => s.displayName,
                        getDescription: (s) => s.description,
                      }}
                    />
                    {skills
                      .filter((s) => config.selectedSkills.includes(s.slug))
                      .filter((s) => s.toolDependencies.length > 0)
                      .map((skill) => (
                        <div
                          key={`autogrants-${skill.id}`}
                          className="ml-1 flex flex-wrap items-center gap-1 text-[9px] text-muted-foreground"
                        >
                          <span>
                            <span className="font-medium">{skill.displayName}</span> auto-grants:
                          </span>
                          {skill.toolDependencies.map((t) => (
                            <Badge key={t} variant="secondary" className="text-[8px] font-mono">{t}</Badge>
                          ))}
                        </div>
                      ))}
                  </>
                )
              ) : (
                // Sysadmin: native label list (no CatalogReferencePicker).
                catalog.skillsEmpty ? (
                  <p className="text-[11px] text-muted-foreground italic">
                    No skills authored yet. Author + publish at <span className="font-medium">Sysadmin → Catalog → Skills</span>.
                  </p>
                ) : (
                  skills.map((skill) => (
                    <label
                      key={skill.id}
                      className={cn(
                        'flex items-start gap-2 p-2 rounded-lg border cursor-pointer',
                        config.selectedSkills.includes(skill.slug)
                          ? 'border-primary/30 bg-primary/5'
                          : 'border-transparent hover:bg-muted/30',
                      )}
                    >
                      <Checkbox
                        checked={config.selectedSkills.includes(skill.slug)}
                        onCheckedChange={() => toggleSkill(skill.slug)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium">{skill.displayName}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{skill.description}</p>
                        {config.selectedSkills.includes(skill.slug) && skill.toolDependencies.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            <span className="text-[9px] text-muted-foreground">Auto-grants:</span>
                            {skill.toolDependencies.map((t) => (
                              <Badge key={t} variant="secondary" className="text-[8px] font-mono">{t}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  ))
                )
              )}
            </div>
          </SectionCard>

          {/* Section 6/7: Knowledge & Guardrails */}
          <SectionCard
            title="Knowledge & Guardrails"
            icon={Shield}
            number={sectionMap.guardrails}
            status={adapter.getSectionStatus(config, sectionMap.guardrails)}
            open={openSections.has(sectionMap.guardrails)}
            onToggle={() => toggleSection(sectionMap.guardrails)}
          >
            <div className="space-y-3">
              <CatalogReferencePicker
                leafKind="knowledge"
                label="Library References"
                description="Select published Library content this agent should use for grounded answers."
                multiSelect
                value={config.knowledgeSources}
                onChange={(next) => updateConfig({ knowledgeSources: next })}
                placeholder="Select Library references..."
                emptyMessage={
                  scope === 'tenant'
                    ? 'Publish a wiki article or upload an indexed document in Library first.'
                    : 'Publish system Library content first.'
                }
                source={{
                  useItems: () => ({
                    data: libraryRefs,
                    isLoading: libraryRefsLoading,
                    error: libraryRefsError,
                  }),
                  getId: (ref) => encodeLibraryRef(ref),
                  getLabel: (ref) => ref.displayName,
                  getDescription: (ref) => `${ref.kind} - ${ref.description}`,
                  getStatus: (ref) => ref.status,
                }}
              />
              <div className="space-y-1.5">
                <Label className="text-xs">Guardrail Profile</Label>
                <RadioGroup
                  value={config.guardrailProfile}
                  onValueChange={(v) => updateConfig({ guardrailProfile: v as GuardrailProfile })}
                  className="space-y-1.5"
                >
                  {([
                    { value: 'basic', label: 'Basic', desc: 'Regex pattern scan only (<40ms)' },
                    { value: 'standard', label: 'Standard', desc: 'Regex + LLM classifier + topic drift (<300ms)' },
                    { value: 'maximum', label: 'Maximum', desc: 'Standard + second-model review (<550ms)' },
                  ] as const).map((opt) => (
                    <label
                      key={opt.value}
                      className={cn(
                        'flex items-start gap-2 p-2 rounded-lg border cursor-pointer text-xs',
                        config.guardrailProfile === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30',
                      )}
                    >
                      <RadioGroupItem value={opt.value} className="mt-0.5" />
                      <div>
                        <p className="font-medium">{opt.label}</p>
                        <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Forbidden Topics (comma-separated)</Label>
                <Input
                  value={config.forbiddenTopics.join(', ')}
                  onChange={(e) => updateConfig({
                    forbiddenTopics: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })}
                  className="h-8 text-xs"
                  placeholder="e.g., financial advice, medical diagnosis"
                />
              </div>
            </div>
          </SectionCard>

          {/* Section 6.5 / 8: Workflows — Bucket B affordance. */}
          {adapter.showWorkflowsPicker === 'enabled' && (
            <SectionCard
              title={`Workflows (${config.selectedWorkflows.length})`}
              icon={WorkflowIcon}
              number={sectionMap.workflows!}
              status={adapter.getSectionStatus(config, sectionMap.workflows!)}
              open={openSections.has(sectionMap.workflows!)}
              onToggle={() => toggleSection(sectionMap.workflows!)}
            >
              <div className="space-y-3">
                <p className="text-[10px] text-muted-foreground">
                  Deterministic playbook workflows this agent can invoke (e.g., shift-handover report generation, troubleshooting sequences).
                </p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input value={workflowSearch} onChange={(e) => setWorkflowSearch(e.target.value)} placeholder="Search workflows..." className="h-7 text-xs pl-8" />
                </div>
                <div className="max-h-[250px] overflow-auto space-y-1">
                  {filteredWorkflows.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic px-1">
                      No workflows authored yet. Author + publish at <span className="font-medium">Sysadmin → Catalog → Workflows</span>.
                    </p>
                  ) : filteredWorkflows.map((w) => (
                    <label
                      key={w.id}
                      className={cn(
                        'flex items-center gap-2 p-1.5 rounded hover:bg-muted/30 cursor-pointer text-xs',
                        config.selectedWorkflows.includes(w.slug) && 'bg-primary/5',
                      )}
                    >
                      <Checkbox
                        checked={config.selectedWorkflows.includes(w.slug)}
                        onCheckedChange={() => toggleWorkflow(w.slug)}
                      />
                      <span className="font-mono text-[11px] flex-1 truncate" title={w.displayName}>{w.slug}</span>
                      <Badge variant="outline" className="text-[8px] capitalize">{w.status}</Badge>
                    </label>
                  ))}
                </div>
              </div>
            </SectionCard>
          )}
          {adapter.showWorkflowsPicker === 'disabled-preview' && (
            <SectionCard
              title={`Workflows (${config.selectedWorkflows.length})`}
              icon={WorkflowIcon}
              number={6.5 as unknown as number}
              status={config.selectedWorkflows.length > 0 ? 'complete' : 'empty'}
              open={openSections.has(tenantWorkflowsSection)}
              onToggle={() => toggleSection(tenantWorkflowsSection)}
            >
              <div className="space-y-2">
                <div
                  className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300 leading-snug"
                  role="status"
                >
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                  <div>
                    <span className="font-medium">Workflows composition (preview).</span>{' '}
                    Wiring agents to published workflows is coming in v1.1. Build the workflow today in Studio → Workflows; attaching it to an agent will land with the tenant catalog clone endpoint.
                  </div>
                </div>
                <CatalogReferencePicker
                  leafKind="workflow"
                  label=""
                  description="Workflows are pre-authored procedure skeletons (PR-K1 catalog). The agent can invoke a workflow to execute a deterministic plan instead of free-form reasoning."
                  multiSelect
                  value={config.selectedWorkflows}
                  onChange={(next) => updateConfig({ selectedWorkflows: next })}
                  disabled
                  source={{
                    useItems: () => ({
                      data: workflows ?? [],
                      isLoading: false,
                      error: undefined,
                    }),
                    getId: (w) => w.slug,
                    getLabel: (w) => w.displayName,
                    getDescription: (w) => `${w.slug} · ${w.category}`,
                    getStatus: (w) => w.status,
                  }}
                />
              </div>
            </SectionCard>
          )}

          {/* Section 7/9: Triggers */}
          <SectionCard
            title="Triggers & Scheduling"
            icon={Zap}
            number={sectionMap.triggers}
            status={adapter.getSectionStatus(config, sectionMap.triggers)}
            open={openSections.has(sectionMap.triggers)}
            onToggle={() => toggleSection(sectionMap.triggers)}
          >
            <div className="space-y-3">
              <div className="space-y-2">
                {([
                  { value: 'conversational' as AgentTrigger, label: 'Conversational', desc: 'User invokes via Ask Argus chat', icon: MessageSquare },
                  { value: 'scheduled' as AgentTrigger, label: 'Scheduled', desc: 'Runs on a cron schedule', icon: Clock },
                  { value: 'event' as AgentTrigger, label: 'Event-triggered', desc: 'Fires on system events', icon: Zap },
                ]).map((opt) => (
                  <div key={opt.value} className="space-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={config.triggerModes.includes(opt.value)}
                        onCheckedChange={() => toggleTrigger(opt.value)}
                      />
                      <opt.icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium">{opt.label}</span>
                      <span className="text-[10px] text-muted-foreground">— {opt.desc}</span>
                    </label>
                    {opt.value === 'scheduled' && config.triggerModes.includes('scheduled') && (
                      <div className="ml-8 space-y-1">
                        <Label className="text-[10px]">Cron Expression</Label>
                        <Input
                          value={config.cronExpression}
                          onChange={(e) => updateConfig({ cronExpression: e.target.value })}
                          className="h-7 text-xs font-mono"
                          placeholder="0 6,18 * * * (every 6am and 6pm)"
                        />
                      </div>
                    )}
                    {opt.value === 'event' && config.triggerModes.includes('event') && (
                      <div className="ml-8 space-y-1">
                        <Label className="text-[10px]">Event Types</Label>
                        <Input
                          value={config.eventTypes.join(', ')}
                          onChange={(e) => updateConfig({
                            eventTypes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                          })}
                          className="h-7 text-xs"
                          placeholder="alarm, work_order_overdue, certification_expiring"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>

          {/* Section 8/10: Advanced Settings */}
          <SectionCard
            title="Advanced Settings"
            icon={Settings}
            number={sectionMap.advanced}
            status="complete"
            open={openSections.has(sectionMap.advanced)}
            onToggle={() => toggleSection(sectionMap.advanced)}
          >
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold mb-2">Memory & Context</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Conversation History</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={config.historyLength}
                        onChange={(e) => updateConfig({ historyLength: Number(e.target.value) })}
                        className="h-8 text-xs"
                        min={5}
                        max={50}
                      />
                      <span className="text-[10px] text-muted-foreground shrink-0">turns</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Context Strategy</Label>
                    <Select
                      value={config.contextStrategy}
                      onValueChange={(v) => updateConfig({ contextStrategy: v as 'sliding_window' | 'summarization' })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sliding_window">Sliding Window</SelectItem>
                        <SelectItem value="summarization">Summarization</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-semibold mb-2">Retrieval (RAG)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Retrieval Count</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={config.retrievalCount}
                        onChange={(e) => updateConfig({ retrievalCount: Number(e.target.value) })}
                        className="h-8 text-xs"
                        min={1}
                        max={20}
                      />
                      <span className="text-[10px] text-muted-foreground shrink-0">chunks</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Similarity Threshold</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={config.similarityThreshold}
                        onChange={(e) => updateConfig({ similarityThreshold: Number(e.target.value) })}
                        className="h-8 text-xs"
                        min={0}
                        max={1}
                        step={0.05}
                      />
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {config.similarityThreshold < 0.6 ? 'Broad' : config.similarityThreshold > 0.8 ? 'Strict' : 'Balanced'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-semibold mb-2">Response Behavior</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Response Length</Label>
                    <Select
                      value={config.responseLengthProfile}
                      onValueChange={(v) => updateConfig({ responseLengthProfile: v as 'brief' | 'standard' | 'detailed' })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="brief">Brief (1-3 sentences)</SelectItem>
                        <SelectItem value="standard">Standard (1-2 paragraphs)</SelectItem>
                        <SelectItem value="detailed">Detailed (full report)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <Switch
                      checked={config.followUpSuggestions}
                      onCheckedChange={(v) => updateConfig({ followUpSuggestions: v })}
                    />
                    <Label className="text-xs">Follow-up suggestions</Label>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-semibold mb-2">Execution Limits</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Timeout</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={config.timeoutSeconds}
                        onChange={(e) => updateConfig({ timeoutSeconds: Number(e.target.value) })}
                        className="h-8 text-xs"
                        min={30}
                        max={300}
                      />
                      <span className="text-[10px] text-muted-foreground shrink-0">sec</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Max Retries</Label>
                    <Input
                      type="number"
                      value={config.maxRetries}
                      onChange={(e) => updateConfig({ maxRetries: Number(e.target.value) })}
                      className="h-8 text-xs"
                      min={0}
                      max={5}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Token Budget</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={config.maxTokenBudget}
                        onChange={(e) => updateConfig({ maxTokenBudget: Number(e.target.value) })}
                        className="h-8 text-xs"
                        min={1000}
                        max={200000}
                        step={1000}
                      />
                      <span className="text-[10px] text-muted-foreground shrink-0">tokens</span>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-semibold mb-2">Escalation</p>
                <div className="space-y-1">
                  <Label className="text-xs">Escalate to Human</Label>
                  <Select
                    value={config.escalationTrigger}
                    onValueChange={(v) => updateConfig({ escalationTrigger: v as typeof config.escalationTrigger })}
                  >
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Never — fully autonomous</SelectItem>
                      <SelectItem value="on_error">On error — when agent fails</SelectItem>
                      <SelectItem value="low_confidence">Low confidence — when uncertain</SelectItem>
                      <SelectItem value="always">Always — every response reviewed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {/*
        #151 — Prompt apply dialog. Mounted at the top level so it
        overlays the entire builder, not just the System Prompt
        section. onApply overwrites the systemPrompt field with the
        substituted body and closes the dialog (dialog handles its own
        onOpenChange(false) call after onApply returns).
      */}
      <PromptApplyDialog
        open={promptApplyOpen}
        onOpenChange={setPromptApplyOpen}
        applyLabel="Use as system prompt"
        onApply={(body) => updateConfig({ systemPrompt: body })}
      />
    </div>
  );
}

// ─── Section Card Component ─────────────────────────────────────────────────

function SectionCard({ title, icon: Icon, number: _n, status, open, onToggle, children }: {
  title: string;
  icon: typeof Bot;
  number: number;
  status: 'empty' | 'partial' | 'complete';
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const StatusIcon = STATUS_ICON[status];
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <Card>
        <CollapsibleTrigger asChild>
          <div className="flex items-center gap-2 px-4 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors">
            <StatusIcon className={cn('h-4 w-4', STATUS_COLOR[status])} />
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold flex-1">{title}</span>
            <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

